import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { loadMe } from '@app/store/actions/profile.actions';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { IContentBlock } from '@core/services/interfaces';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import { FindAccountStrategyEnum, IFindAccountsFilter, IPagedResults, IPagination, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountDetailsType, AccountService, MetaService, PlatformService } from '../../core/services/';
import { UsersFormService } from '../users-form.service';

type UsersBlockType = IContentBlock<User, IFindAccountsFilter>;
type UsersLoaderType = (
  filter: Partial<IFindAccountsFilter>,
  pagination: IPagination,
) => Observable<IPagedResults<User[]>>;

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent extends ChangableComponent implements OnInit, OnDestroy {
  public users: UsersBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 12,
      total: 0,
      totalPages: 0,
    },
  };
  private details: AccountDetailsType[] = [
    'id',
    'slug',
    'first_name',
    'last_name',
    'country',
    'userpic_id',
    'visit_info',
  ];
  public paginationEnabled = false;
  public mode: string;
  private paginationTimeout: Subscription;
  private me: User;
  private usersLoader: UsersLoaderType;

  constructor(
    private router: Router,
    private accountService: AccountService,
    private usersForm: UsersFormService,
    private loadingBar: LoadingBarService,
    private meta: MetaService,
    private store: Store,
    protected readonly platform: PlatformService,
    protected readonly cdr: ChangeDetectorRef,
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr, platform);
  }

  ngOnInit(): void {
    this.setupByRoute(this.router.url);

    this.usersForm.form$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (form) => {
        if (this.users.filter.term !== form.term) {
          this.users.pagination.page = 1;
        }
        this.users.filter.term = form.term;
        this.loadUsers();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    this.loadUsers();
    this.setupMeta();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.meta.reset();
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this.me);
  }

  private setupByRoute(url: string) {
    switch (url) {
      case '/users/subscribers':
        this.usersLoader = this.accountService.getSubscribers;
        this.mode = 'subscribers';
        break;

      case '/users/subscriptions':
        this.usersLoader = this.accountService.getSubscriptions;
        this.mode = 'subscriptions';
        break;

      case '/users/students':
        this.usersLoader = this.accountService.findAccounts;
        this.mode = 'students';
        break;

      default:
        this.usersLoader = this.accountService.findAccounts;
        this.mode = 'all';
        break;
    }
  }

  public loadUsers(nextPage?: boolean) {
    if (this.users.loading) {
      return;
    }
    this.users.loading = true;
    this.loadingBar.fetchingStart(35);
    if (nextPage && this.users.pagination.page < this.users.pagination.totalPages) {
      this.users.pagination.page = this.users.pagination.page + 1;
    }

    let usersLoader: Observable<IPagedResults<User[]>>;
    switch (this.mode) {
      case 'subscribers':
        usersLoader = this.accountService.getSubscribers(this.users.filter, this.users.pagination, this.details);
        break;

      case 'subscriptions':
        usersLoader = this.accountService.getSubscriptions(this.users.filter, this.users.pagination, this.details);
        break;

      case 'students':
        usersLoader = this.accountService.findAccounts(
          { use: FindAccountStrategyEnum.my_students },
          this.users.pagination,
          this.details,
        );
        break;

      case 'all':
        usersLoader = this.accountService.findAccounts(this.users.filter, this.users.pagination, this.details);
    }

    usersLoader.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        if (nextPage) {
          // сбоит this.users.items = [ ...this.users.items, ...response.results ];
          response.results.map((t) => {
            if (!this.users.items.find((t1) => t1.id === t.id)) {
              this.users.items.push(t);
            }
          });
        } else {
          this.users.items = response.results;
        }
        this.users.pagination.total = response.total;
        this.users.pagination.totalPages = response.totalPages;
        this.users.pagination.page = response.page;
        this.users.loaded = true;
        this.users.loading = false;
        if (this.isBrowser) {
          this.paginationTimeout = this.setTimeout(() => (this.paginationEnabled = true), 1500);
        } else {
          this.paginationEnabled = true;
        }
        this.loadingBar.fetchingComplete();
      },
      error: (error: any) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.users.loading = false;
        this.loadingBar.fetchingStop();
      },
    });
  }

  // функция для трекинга списка, чтобы он не 'моргал'
  public trackByFn(index, user: User): string {
    return user.id;
  }

  public nextPage() {
    this.loadUsers(true);
  }

  private setupMeta() {
    switch (this.mode) {
      case 'subscribers':
        this.meta.setTitle(['users.meta-title', 'users.meta-subscribers-title']);
        // TODO meta
        break;

      case 'subscriptions':
        this.meta.setTitle(['users.meta-title', 'users.meta-subscriptions-title']);
        // TODO meta
        break;

      case 'students':
        this.meta.setTitle(['users.meta-title', 'users.meta-my-students-title']);
        // TODO meta
        break;

      case 'all':
        this.meta.setTitle(['users.meta-title']);
      // TODO meta
    }
  }
}
